import api from 'api';
import moment from 'moment';

import { ingredientsSearchTabs } from 'constants/ingredientsSearch';
import { ingredientTypeMap } from 'constants/ingredientType';
import { BRAND_PROPERTIES, BRAND_PROPERTY_TYPES } from 'constants/brand';
import { inputDateFormat, inputDateFormatTS } from 'constants/globalPrice';
import { getPriceEffectiveDate, shouldFetchSyscoProducts } from 'helpers/globalPriceHelper';
import { getCurrentUserBrand } from 'selectors/user-brands';
import { createBrandPropertyAsync } from 'actions/user-brands';

export const PAGINATED_PRODUCT_UPDATE_SUCCESS = 'PAGINATED_PRODUCT_UPDATE_SUCCESS';
export const PREVIOUSLY_USED_ASYNC_UPDATE_SUCCESS = 'PREVIOUSLY USED ASYNC_UPDATE_SUCCESS';
export const LIST_INGREDIENT_ASYNC_UPDATE_SUCCESS = 'LIST_INGREDIENT_ASYNC_UPDATE_SUCCESS';
export const BATCH_RECIPES_ASYNC_UPDATE_SUCCESS = 'BATCH_RECIPES_ASYNC_UPDATE_SUCCESS';
export const SET_LOADING_INGREDIENTS_SEARCH_STATUS = 'SET_LOADING_INGREDIENTS_SEARCH_STATUS';
export const UPDATE_ORDER_GUIDE_PRICE = 'UPDATE_ORDER_GUIDE_PRICE';
export const UPDATE_INGREDIENTS_PRICE = 'UPDATE_INGREDIENTS_PRICE';
export const SET_SELECTED_TAB = 'SET_SELECTED_TAB';
export const ADD_FILTER = 'ADD_FILTER';
export const RESET_ALL = 'RESET_ALL';
export const RESET_ALL_FILTERS = 'RESET_ALL_FILTERS';
export const RESET_TAB_FILTERS = 'RESET_TAB_FILTERS';
export const REMOVE_FILTER = 'REMOVE_FILTER';
export const UPDATE_SELECTED_ITEMS = 'UPDATE_SELECTED_ITEMS';
export const CLEAR_SELECTED_ITEMS = 'CLEAR_SELECTED_ITEMS';
export const REMOVE_INGREDIENT_FROM_SELECTED_ITEMS = 'REMOVE_INGREDIENT_FROM_SELECTED_ITEMS';
export const UPDATE_INGREDIENT_QTY = 'UPDATE_INGREDIENT_QTY';
export const INGREDIENT_COST_ASYNC_GET_SUCCESS = 'INGREDIENT_COST_ASYNC_GET_SUCCESS';
export const UPDATE_INGREDIENT_UNIT = 'UPDATE_INGREDIENT_UNIT';
export const UPDATE_SORT_PROPS = 'UPDATE_SORT_PROPS';
export const UPDATE_SEARCH_KEYWORD = 'UPDATE_SEARCH_KEYWORD';
export const UPDATE_SEARCHED_TABS = 'UPDATE_SEARCHED_TABS';
export const RESET_TAB = 'RESET_TAB';
export const ADD_NEW_CUSTOM_INGREDIENT = 'ADD_NEW_CUSTOM_INGREDIENT';
export const ADD_INGREDIENT_TO_SELECTED_ITEMS = 'ADD_INGREDIENT_TO_SELECTED_ITEMS';
export const CHECK_BATCH_RECIPE_CIRCULAR_DEPENDENCY_ERROR = 'CHECK_BATCH_RECIPE_CIRCULAR_DEPENDENCY_ERROR';

const setLoadingStatus = (isLoading) => {
  return {
    type: SET_LOADING_INGREDIENTS_SEARCH_STATUS,
    isLoading,
  };
};

const paginatedProductUpdateSuccess = (tabKey, data) => {
  return {
    type: PAGINATED_PRODUCT_UPDATE_SUCCESS,
    data: { tabKey, data }
  };
};

/**
 * action creator for previously used
 * @param {Object} data previously used response (prepared) data
 * @param {String} ingredType ingredient type CI-customer Ingredient SP-sysco product
 */
const previoslyUsedAsyncUpdateSuccess = (data, ingredType = ingredientTypeMap.SYSCO_PRODUCTS) => {
  const { previouslyUsedIngredients, ...rest } = data;

  const priceUpdatedProducts = previouslyUsedIngredients && previouslyUsedIngredients.map(ingredient => ({
    ...ingredient,
    unitCost: ingredient.totalCost,
  }));

  let previouslyUsedData = {
    ...rest,
    products: priceUpdatedProducts,
  };
  return {
    type: PREVIOUSLY_USED_ASYNC_UPDATE_SUCCESS,
    data: { previouslyUsedData, ingredType }
  };
};

const listAsyncUpdateSuccess = (data) => {
  const { listIngredients, ...rest } = data;
  let listIngredientsData = {
    ...rest,
    products: listIngredients,
  };
  return {
    type: LIST_INGREDIENT_ASYNC_UPDATE_SUCCESS,
    listIngredientsData,
  };
};
function batchRecipesAsyncUpdateSuccess(products, loaded) {
  const filteredProducts = products.filter(product => product.yield && !product.archived);
  return {
    type: BATCH_RECIPES_ASYNC_UPDATE_SUCCESS,
    data: { products: filteredProducts, loaded, facets: [] },
  };
}

const updateIngredientsPrice = (prices, tab) => {
  return {
    type: UPDATE_INGREDIENTS_PRICE,
    data: { prices, tab },
  };
};

/**
 * Action creator to set selected tab and selected tab data
 * @param {*} tab
 */
export const setSelectedTab = (tab) => {
  return {
    type: SET_SELECTED_TAB,
    data: tab,
  };
};

/**
 * Action creator to update filter selected/unselected
 * @param {*} tab
 * @param {*} facetId
 * @param {*} filterId
 * @param {*} isSelected
 */
export const updateFilters = (tab, facetId, filterId, isSelected) => {
  return {
    type: isSelected ? ADD_FILTER : REMOVE_FILTER,
    data: { tab, facetId, filterId },
  };
};

/**
 * Action for reset to the initial state
 */
export const resetAll = () => {
  return {
    type: RESET_ALL,
  };
};

/**
 * Action creator for resetting all filters in ingredients search
 */
export const resetAllFilters = () => {
  return {
    type: RESET_ALL_FILTERS,
  };
};

/**
 * Action creator for resetting all filters in a ingredients search tab
 * @param {*} tab 
 */
export const resetTabFilters = (tab) => {
  return {
    type: RESET_TAB_FILTERS,
    data: tab,
  };
};

/**
 * Fetch prices for given ingredients list
 * @param {*} tab
 */
export const fetchPrice = (tab) => {
  return (dispatch, getState) => {

    const ingredients = getState().ingredientsSearch[tab].products;
    if (ingredients.length === 0) return Promise.resolve();
    const args = ingredients.map(ingredient => {
      return {
        supc: ingredient.supc,
        isSoldAs_split: ingredient.isSoldAs_split,
      };
    });
    return api.request('price', 'post', args).then((data) => {
      const prices = data.map(priceItem => {
        return {
          supc: priceItem.supc,
          unitCost: priceItem.price,
          priceLoaded: true,
          priceLoadSuccess: true,
          priceStatuses: priceItem.statuses,
        };
      });
      dispatch(updateIngredientsPrice(prices, tab));
    }).catch(() => {
      const prices = ingredients.map(ingredient => {
        return {
          supc: ingredient.supc,
          priceLoaded: true,
          priceLoadSuccess: false,
        };
      });
      dispatch(updateIngredientsPrice(prices, tab));
    });
  };
};

/**
 * Fetch purchase history for ingredients search
 * @param {*} purchasedWithinWeeks
 */
export const getPurchaseHistory = (purchasedWithinWeeks) => {
  return (dispatch, getState) => {
    dispatch(setLoadingStatus(true));
    return api.request(`orderGuide?purchasedWithinWeeks=${purchasedWithinWeeks}`, 'get')
      .then(data => dispatch(paginatedProductUpdateSuccess(ingredientsSearchTabs.PURCHASE_HISTORY_TAB.key,
        {
          ...data,
          loaded: true,
          sort: { sorted: false }
        })))
      .then(() => dispatch(fetchPrice(ingredientsSearchTabs.PURCHASE_HISTORY_TAB.key)))
      .catch(err => {
        dispatch(paginatedProductUpdateSuccess(ingredientsSearchTabs.PURCHASE_HISTORY_TAB.key,
          {
            facets: [],
            firstRecNum: 0,
            lastRecNum: 0,
            totalNumRecs: 0,
            products: [],
            opcoCustomerDate: '',
            loaded: false,
          }));
        console.log("Fetch purchase history Error: ", err);
      })
      .finally(() => dispatch(setLoadingStatus(false)));
  };
};

/**
 * Fetch Ingredients Batch Recipes for Ingredients Search 
 * @param {*} brandId 
 * @param {*} batchLevel 
 */
export function getBatchRecipes(brandId, batchLevel = null) {
  return (dispatch, getState) => {
    dispatch(setLoadingStatus(true));
    let apilink;
    if (null != batchLevel) {
      apilink = `recipe/list?brandId=${brandId}&fetchBatch=true&batchLevel=${batchLevel}`;
    } else {
      apilink = `recipe/list?brandId=${brandId}&fetchBatch=true`;
    }

    return api.request(apilink, 'get')
      .then(data => dispatch(batchRecipesAsyncUpdateSuccess(data, true)))
      .catch(err => {
        dispatch(batchRecipesAsyncUpdateSuccess([], false));
        console.log("Fetch Ingredients Batch Recipes Error: ", err);
      })
      .finally(() => dispatch(setLoadingStatus(false)));
  };
}

export const addIngredientToSelectedList = (ingredient) => {
  return {
    type: ADD_INGREDIENT_TO_SELECTED_ITEMS,
    data: { ingredient }
  };
};

/**
 * clear selected items from store
 */
export const clearSelectedItems = () => {
  return {
    type: CLEAR_SELECTED_ITEMS,
  };
};

export const removeIngredientFromSelectedList = (removedIngredient) => {
  return {
    type: REMOVE_INGREDIENT_FROM_SELECTED_ITEMS,
    data: removedIngredient
  };
};

export const handleIngredientQtyUpdate = (ingredient, qty, isQtyValid) => {
  return (dispatch, getState) => {
    dispatch(updateIngredientQty(ingredient.uniqKey, qty, isQtyValid));

    const selectedIngredients = getState().ingredientsSearch.selectedIngredients;
    const product = selectedIngredients.find(selectedIngredient => {
      return selectedIngredient.uniqKey === ingredient.uniqKey
    });
    dispatch(fetchIngredientCost(product));
  }
};

export const handleIngredientUnitUpdate = (ingredient, unit) => {
  return (dispatch, getState) => {
    dispatch(updateIngredientUnit(ingredient.uniqKey, unit.value));

    const selectedIngredients = getState().ingredientsSearch.selectedIngredients;
    const product = selectedIngredients.find(selectedIngredient => {
      return selectedIngredient.uniqKey === ingredient.uniqKey
    });
    dispatch(fetchIngredientCost(product));
  }
};

const ingredientCostAsyncGetSuccess = (data) => {
  return {
    type: INGREDIENT_COST_ASYNC_GET_SUCCESS,
    data,
  };
};

/**
 * Fetch previously used ingredients. Could be either sysco products or customer ingredients
 * TODO: unit test, test catch
 * @param {*} brandId
 * @param {String} ingredType ingredient type CI-customer Ingredient SP-sysco product
 */
export const getPreviouslyUsed = (brandId, ingredType) => {
  return (dispatch, getState) => {
    dispatch(setLoadingStatus(true));
    return api.request(`ingredient/previouslyUsed?brandId=${brandId}&ingredType=${ingredType}`, 'get')
      .then(data => dispatch(previoslyUsedAsyncUpdateSuccess({
        ...data,
        loaded: true,
        sort: { sorted: false }
      }, ingredType)))
      .catch(() => {
        dispatch(previoslyUsedAsyncUpdateSuccess({
          facets: [],
          previouslyUsedIngredients: [],
          loaded: false,
        }, ingredType));
      })
      .finally(() => dispatch(setLoadingStatus(false)));
  };
};

/**
 * Fetch all list item ingredients
 * TODO: unit test, test catch
 * @param {*} listId
 */
export const getListIngredient = (listId) => {
  return (dispatch, getState) => {
    dispatch(setLoadingStatus(true));
    let apilink;
    if (listId === 'all') {
      apilink = `listSearch`;
    } else {
      apilink = `listSearch/${listId}`;
    }
    return api.request(apilink, 'get')
      .then(data => dispatch(listAsyncUpdateSuccess({
        ...data,
        loaded: true,
        sort: { sorted: false }
      })))
      .then(() => dispatch(fetchPrice(ingredientsSearchTabs.ITEM_LISTS_TAB.key)))
      .catch(() => {
        dispatch(listAsyncUpdateSuccess({
          facets: [],
          listIngredients: [],
          loaded: false,
        }));
      })
      .finally(() => dispatch(setLoadingStatus(false)));
  };
};

export const fetchIngredientCost = (ingredient) => {
  return (dispatch, getState) => {

    return api.request(`ingredient/getTotalCostByUnits`, 'post', ingredient)
      .then(data => {
        dispatch(ingredientCostAsyncGetSuccess({ ...data, ingredientUniqKey: ingredient.uniqKey }));
        return data;
      })
      .catch(() => {
        dispatch(ingredientCostAsyncGetSuccess({ costPerServing: 0, ingredientUniqKey: ingredient.uniqKey }));
      });
  };
};

export const updateIngredientQty = (ingredientUniqKey, qty, isQtyValid) => {
  return {
    type: UPDATE_INGREDIENT_QTY,
    data: { ingredientUniqKey, qty, isQtyValid }
  };
};

export const updateIngredientUnit = (ingredientUniqKey, unitCode) => {
  return {
    type: UPDATE_INGREDIENT_UNIT,
    data: { ingredientUniqKey, unitCode }
  };
};

/**
 * save selected items in DB
 * @param {List} selectedItems selected items list of objects
 */
export const addToRecipe = selectedItems => {
  return (dispatch, getState) => {
    const syscoProducts = getState().syscoProducts.get('data');
    const currentBrand = getCurrentUserBrand(getState());
    const user = getState().user.get('data');

    dispatch(setLoadingStatus(true));
    // TODO: refactor - remove Promise, use error callback, add finally
    return new Promise((resolve, reject) =>
      api.request(`ingredient/batch`, 'post', selectedItems)
        .then(() => {
          dispatch(setLoadingStatus(false));
          const isNoSyscoProducts = shouldFetchSyscoProducts(user, syscoProducts); // only for US user
          const isSyscoProductAdded = selectedItems.some(i => !!i.supc);
          if (!getPriceEffectiveDate(currentBrand) && isNoSyscoProducts && isSyscoProductAdded) {
            const brandProperty = {
              propertyType: BRAND_PROPERTY_TYPES.GLOBAL_PRICE_PROPERTY_TYPE,
              propertyKey: BRAND_PROPERTIES.GLOBAL_PRICE_EFFECTIVE_DATE,
              propertyValue: moment.utc().format(inputDateFormat),
            };
            dispatch(createBrandPropertyAsync(brandProperty, currentBrand.get('id'), moment.utc().format(inputDateFormatTS)));
          }
          return resolve();
        })
        .catch(err => {
          dispatch(setLoadingStatus(false));
          return reject(err);
        })
    );

  }
};

/**
 * update selected items in the store
 * @param {List} selectedItems selected items list of objects
 */
export const updateSelectedIngredients = selectedItems => {
  return {
    type: UPDATE_SELECTED_ITEMS,
    data: selectedItems
  };
};

/**
 * update product list for a given specific tab
 * @param {String} tabKey tabKey of the products
 * @param {Object} sort sort properties eg: {sorted:true, sortColumn:'a', sortDirection:'ASC'}
 */
export const updateSortProps = (tabKey, sort) => {
  return {
    type: UPDATE_SORT_PROPS,
    data: { tabKey, sort }
  }
};

/**
 * Update the store with the searched text
 * @param {String} keyword keyword to be searched
 * @param {Boolean} isSupc whether a number or not
 */
export const updateSearchKeyword = (keyword, isSupc, searchedTabs) => {
  return {
    type: UPDATE_SEARCH_KEYWORD,
    data: { keyword: keyword || '', isSupc, searchedTabs: searchedTabs || [] }
  }
};

/**
 * Add searched tab to the searched Tabs
 * @param {String} searchedTabs updated searchedTabs
 */
export const updateSearchedTabs = searchedTabs => {
  return {
    type: UPDATE_SEARCHED_TABS,
    data: searchedTabs || []
  }
};

/**
 * fetch product catalog details
 * @param {String} opcoId opco id
 * @param {String} syscoCustomerId customer id
 * @param {String} keyword search string
 */
export const getProductCatalogue = keyword => {
  return (dispatch, getState) => {
    dispatch(setLoadingStatus(true));
    return api.request(
      `fullproductcatalog?productName=${keyword}`,
      'get',
      null
    )
      .then(data =>
        dispatch(paginatedProductUpdateSuccess(ingredientsSearchTabs.FULL_PRODUCT_CATALOG_TAB.key,
          {
            ...data,
            sort: { sorted: false }
          })))
      .then(() => dispatch(fetchPrice(ingredientsSearchTabs.FULL_PRODUCT_CATALOG_TAB.key)))
      .catch(() =>
        dispatch(paginatedProductUpdateSuccess(ingredientsSearchTabs.FULL_PRODUCT_CATALOG_TAB.key,
          {
            facets: [],
            firstRecNum: 0,
            lastRecNum: 0,
            totalNumRecs: 0,
            products: [],
            loading: false,
          })))
      .finally(() => dispatch(setLoadingStatus(false)));
  };
};

/**
 * Reset products and filters for a given tab
 * @param {String} tabKey tabkey name
 */
export const resetTab = tabKey => {
  return {
    type: RESET_TAB,
    data: tabKey
  }
}

/**
 * Add new custom ingredient
 * @param {Object} ingredient ingredient object
 */
export const addNewCustomIngredient = ingredient => {
  const mappedIngredient = {
    ...ingredient,
    name: ingredient.ingredientName,
    brand: ingredient.ingredientBrand,
    cost: `$${ingredient.costPerUnit}/${ingredient.baseUnitTextKey}`,
  }
  return {
    type: ADD_NEW_CUSTOM_INGREDIENT,
    data: { ingredient: mappedIngredient }
  }
}

/**
 * save ingredient in DB
 * @param {Object} ingredient ingredient object
 */
export const saveNewCustomIngredient = (ingredient, errorCallback) => {
  return (dispatch, getState) => {


    dispatch(setLoadingStatus(true));
    return api.request(`ingredient/independent/addOnce`, 'post', ingredient)
      .then(data => dispatch(addNewCustomIngredient({ ...data, key: ingredient.key })))
      .catch(errorCallback)
      .finally(() => dispatch(setLoadingStatus(false)));
  }
};

const checkBatchRecipeCircularDependencyError = (error) => {
  return {
    type: CHECK_BATCH_RECIPE_CIRCULAR_DEPENDENCY_ERROR,
    payload: error,
  };
};

export const checkForCircularDependencies = (recipeId, batchRecipeId) => {
  return (dispatch) => {
    return api.request(`ingredient/checkForCircularDependencies?recipeId=${recipeId}&batchRecipeId=${batchRecipeId}`, 'post', null)
      .then((data) => {
        return data;
      })
      .catch(err => {
        console.log('Error :', err.message);
        dispatch(checkBatchRecipeCircularDependencyError(err && err.message));
        throw err;
      })
  }
};
